import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MessageSquare, Send, Search, User as UserIcon, Sun, Flame, Wrench, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useNavigate } from "react-router-dom";
import { ProjectNotes } from "@/components/ProjectNotes";

function fmtWhen(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const midnight = today.getTime();
  if (d.getTime() >= midnight) return d.toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" });
  const yst = midnight - 86400000;
  if (d.getTime() >= yst) return "Gestern";
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" });
}

function ROLE_LABEL(r) {
  return { admin: "Admin", innendienst: "Innendienst", monteur: "Monteur", auftraggeber: "Auftraggeber" }[r] || r;
}

export default function Nachrichten() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [partners, setPartners] = useState([]);
  const [projectThreads, setProjectThreads] = useState([]);
  // activeKey Format: "u:<userId>" oder "p:<projectId>"
  const [activeKey, setActiveKey] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [q, setQ] = useState("");
  const bottomRef = useRef(null);

  const activeType = activeKey?.[0]; // 'u' | 'p'
  const activeId = activeKey?.slice(2);

  const loadPartners = async () => {
    try {
      const [u, pt] = await Promise.all([
        api.get("/messages/users").then((x) => x.data).catch(() => []),
        api.get("/messages/project-threads").then((x) => x.data).catch(() => []),
      ]);
      setPartners(u || []);
      setProjectThreads(pt || []);
      if (!activeKey) {
        if (u?.length) setActiveKey(`u:${u[0].id}`);
        else if (pt?.length) setActiveKey(`p:${pt[0].project_id}`);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Nachrichten konnten nicht geladen werden");
    }
  };

  const loadThread = async (otherId) => {
    if (!otherId) return;
    try {
      const { data } = await api.get(`/messages/thread/${otherId}`);
      setMessages(data || []);
      await api.post(`/messages/thread/${otherId}/read`).catch(() => {});
      loadPartners();
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Konversation konnte nicht geladen werden");
    }
  };

  useEffect(() => { loadPartners(); }, []);
  useEffect(() => {
    if (activeType === "u" && activeId) loadThread(activeId);
    else setMessages([]);
  }, [activeKey]);

  // Poll every 15s for new messages / partners
  useEffect(() => {
    const t = setInterval(() => {
      loadPartners();
      if (activeType === "u" && activeId) loadThread(activeId);
    }, 15000);
    return () => clearInterval(t);
  }, [activeKey]);

  const activePartner = activeType === "u" ? partners.find((p) => p.id === activeId) : null;
  const activeProject = activeType === "p" ? projectThreads.find((p) => p.project_id === activeId) : null;

  const send = async () => {
    const text = body.trim();
    if (!text || activeType !== "u" || !activeId) return;
    setSending(true);
    try {
      const { data } = await api.post("/messages", { to_user_id: activeId, body: text });
      setMessages((m) => [...m, data]);
      setBody("");
      loadPartners();
      setTimeout(() => bottomRef.current?.scrollIntoView({ behavior: "smooth" }), 30);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Senden fehlgeschlagen");
    } finally {
      setSending(false);
    }
  };

  const filteredPartners = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return partners;
    return partners.filter((p) =>
      (p.name || "").toLowerCase().includes(term) ||
      (p.email || "").toLowerCase().includes(term) ||
      (p.last_message || "").toLowerCase().includes(term)
    );
  }, [partners, q]);

  const filteredProjects = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return projectThreads;
    return projectThreads.filter((p) =>
      (p.kunde_name || "").toLowerCase().includes(term) ||
      (p.last_message || "").toLowerCase().includes(term)
    );
  }, [projectThreads, q]);

  return (
    <div className="p-4 lg:p-8 space-y-4 animate-fade-up" data-testid="nachrichten-page">
      <div>
        <div className="overline">Team-Chat</div>
        <h1 className="font-heading text-3xl lg:text-4xl font-bold tracking-tight mt-2">Nachrichten</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          Interne Nachrichten zwischen Team-Mitgliedern und Partnern.
        </p>
      </div>

      <Card className="border-border overflow-hidden">
        <div className="grid grid-cols-1 md:grid-cols-[280px_1fr] h-[65vh] min-h-[420px]">
          {/* Partner list */}
          <div className="border-r border-border flex flex-col min-h-0">
            <div className="p-3 border-b border-border">
              <div className="relative">
                <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
                <Input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Person suchen…"
                  className="pl-8 h-9"
                  data-testid="msg-search"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {filteredPartners.length === 0 && filteredProjects.length === 0 && (
                <div className="p-4 text-xs text-muted-foreground text-center">Keine Chats verfügbar.</div>
              )}

              {/* Sektion: Personen */}
              {filteredPartners.length > 0 && (
                <>
                  <div className="px-3 py-1.5 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1" data-testid="msg-section-people">
                    <UserIcon className="h-3 w-3" /> Personen
                  </div>
                  {filteredPartners.map((p) => {
                    const key = `u:${p.id}`;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setActiveKey(key)}
                        className={`w-full text-left px-3 py-3 border-b border-border/60 hover:bg-secondary/60 transition-colors ${activeKey === key ? "bg-secondary" : ""}`}
                        data-testid={`msg-partner-${p.id}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className="h-9 w-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                            <UserIcon className="h-4 w-4" />
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-medium truncate">{p.name || p.email}</div>
                              {p.last_at && <div className="text-[10px] text-muted-foreground shrink-0">{fmtWhen(p.last_at)}</div>}
                            </div>
                            <div className="flex items-center justify-between gap-2 mt-0.5">
                              <div className="text-[11px] text-muted-foreground truncate">
                                {p.last_message || <span className="italic">Noch keine Nachrichten</span>}
                              </div>
                              {p.unread > 0 && (
                                <span className="rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1.5 min-w-[1.25rem] h-4 inline-flex items-center justify-center shrink-0" data-testid={`msg-unread-${p.id}`}>
                                  {p.unread}
                                </span>
                              )}
                            </div>
                            <div className="text-[9px] uppercase tracking-wider text-muted-foreground mt-1">{ROLE_LABEL(p.role)}</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </>
              )}

              {/* Sektion: Projekte */}
              {filteredProjects.length > 0 && (
                <>
                  <div className="px-3 py-1.5 bg-muted/40 text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1" data-testid="msg-section-projects">
                    <Wrench className="h-3 w-3" /> Projekte
                  </div>
                  {filteredProjects.map((p) => {
                    const key = `p:${p.project_id}`;
                    return (
                      <button
                        key={key}
                        type="button"
                        onClick={() => setActiveKey(key)}
                        className={`w-full text-left px-3 py-3 border-b border-border/60 hover:bg-secondary/60 transition-colors ${activeKey === key ? "bg-secondary" : ""}`}
                        data-testid={`msg-project-${p.project_id}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${p.project_type === "pv" ? "bg-primary/10 text-primary" : "bg-orange-100 dark:bg-orange-900/20 text-orange-500"}`}>
                            {p.project_type === "pv" ? <Sun className="h-4 w-4" /> : <Flame className="h-4 w-4" />}
                          </div>
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center justify-between gap-2">
                              <div className="font-medium truncate">{p.kunde_name}</div>
                              {p.last_at && <div className="text-[10px] text-muted-foreground shrink-0">{fmtWhen(p.last_at)}</div>}
                            </div>
                            <div className="flex items-center justify-between gap-2 mt-0.5">
                              <div className="text-[11px] text-muted-foreground truncate">
                                {p.last_by && <span className="font-medium">{p.last_by}: </span>}
                                {p.last_message || <span className="italic">Noch keine Nachrichten</span>}
                              </div>
                              {p.unread > 0 && (
                                <span className="rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold px-1.5 min-w-[1.25rem] h-4 inline-flex items-center justify-center shrink-0" data-testid={`msg-project-unread-${p.project_id}`}>
                                  {p.unread}
                                </span>
                              )}
                            </div>
                            <div className="text-[9px] uppercase tracking-wider text-muted-foreground mt-1">Projekt-Chat</div>
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </>
              )}
            </div>
          </div>

          {/* Thread */}
          <div className="flex flex-col min-h-0">
            {activePartner ? (
              <>
                <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                  <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center">
                    <UserIcon className="h-4 w-4" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium truncate">{activePartner.name || activePartner.email}</div>
                    <div className="text-[11px] text-muted-foreground">{ROLE_LABEL(activePartner.role)} · {activePartner.email}</div>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-4 space-y-2 bg-secondary/20">
                  {messages.length === 0 ? (
                    <div className="h-full flex flex-col items-center justify-center text-muted-foreground text-sm gap-2">
                      <MessageSquare className="h-8 w-8 opacity-40" />
                      Noch keine Nachrichten — schreib die erste!
                    </div>
                  ) : messages.map((m) => {
                    const own = m.from_user_id === user?.id;
                    return (
                      <div key={m.id} className={`flex ${own ? "justify-end" : "justify-start"}`} data-testid={`msg-${m.id}`}>
                        <div className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${own ? "bg-primary text-primary-foreground" : "bg-card border border-border"}`}>
                          <div className="whitespace-pre-wrap break-words">{m.body}</div>
                          <div className={`text-[10px] mt-1 ${own ? "text-primary-foreground/70" : "text-muted-foreground"}`}>{fmtWhen(m.created_at)}</div>
                        </div>
                      </div>
                    );
                  })}
                  <div ref={bottomRef} />
                </div>
                <form
                  className="p-3 border-t border-border flex items-end gap-2"
                  onSubmit={(e) => { e.preventDefault(); send(); }}
                >
                  <textarea
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                    placeholder="Nachricht schreiben… (Enter = senden, Shift+Enter = neue Zeile)"
                    rows={2}
                    className="flex-1 rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring resize-none"
                    data-testid="msg-input"
                  />
                  <Button type="submit" disabled={sending || !body.trim()} data-testid="msg-send">
                    <Send className="h-4 w-4 mr-1" /> Senden
                  </Button>
                </form>
              </>
            ) : activeProject ? (
              <>
                <div className="px-4 py-3 border-b border-border flex items-center gap-2">
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center ${activeProject.project_type === "pv" ? "bg-primary/10 text-primary" : "bg-orange-100 dark:bg-orange-900/20 text-orange-500"}`}>
                    {activeProject.project_type === "pv" ? <Sun className="h-4 w-4" /> : <Flame className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{activeProject.kunde_name}</div>
                    <div className="text-[11px] text-muted-foreground">Projekt-Chat · Nachrichten aus den Projekt-Notizen</div>
                  </div>
                  <button
                    onClick={() => navigate(`/projekte?open=${activeProject.project_id}`)}
                    className="text-xs text-primary hover:underline inline-flex items-center gap-1 shrink-0"
                    data-testid="msg-project-open"
                  >
                    Zum Projekt <ExternalLink className="h-3 w-3" />
                  </button>
                </div>
                <div className="flex-1 overflow-y-auto p-4 bg-secondary/20" data-testid="msg-project-thread">
                  <ProjectNotes projectId={activeProject.project_id} />
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-muted-foreground text-sm">
                Wähle einen Chat aus der Liste links.
              </div>
            )}
          </div>
        </div>
      </Card>
    </div>
  );
}
